import { ApiError } from '@apis/contracts';
import type { MessageKey, Translate } from '@apis/i18n';

/** Conflicts the user can act on: the server names them in `details.reason`. */
const CONFLICT_REASONS: Record<string, MessageKey> = {
  bill_active: 'error.billActive',
  bill_has_payments: 'error.billHasPayments',
  items_served: 'error.itemsServed',
};

/** Message key for a failed request, branching on the stable `code` (never on `message`). */
export function errorMessageKey(error: unknown): MessageKey {
  if (error instanceof ApiError) {
    switch (error.code) {
      case 'VALIDATION_FAILED':
        return 'error.validation';
      case 'VERSION_CONFLICT':
        return 'error.versionConflict';
      case 'CONFLICT':
        return CONFLICT_REASONS[String(error.details.reason)] ?? 'error.conflict';
      case 'FORBIDDEN':
        return 'error.forbidden';
      case 'NOT_FOUND':
        return 'error.notFound';
      case 'UNAUTHENTICATED':
        return 'error.unauthenticated';
      case 'RATE_LIMITED':
        return 'error.rateLimited';
      case 'IDEMPOTENCY_IN_PROGRESS':
        return 'error.inProgress';
      case 'BUDGET_EXCEEDED':
        return 'error.budgetExceeded';
      case 'APPROVAL_REQUIRED':
        return 'error.approvalRequired';
      // Plan and subscription lifecycle (ADR-0030/0033)
      case 'SUBSCRIPTION_READ_ONLY':
        return 'error.subscriptionReadOnly';
      case 'MODULE_NOT_IN_PLAN':
        return 'error.moduleNotInPlan';
      case 'PLAN_LIMIT_REACHED':
        return 'error.planLimitReached';
      case 'TENANT_SUSPENDED':
        return 'error.tenantSuspended';
      case 'MFA_REQUIRED':
        return error.details.reason === 'platform_mfa_setup_required'
          ? 'error.platformMfaRequired'
          : 'error.generic';
    }
    if (error.status === 403) return 'error.forbidden';
    if (error.status === 404) return 'error.notFound';
    if (error.status === 401) return 'error.unauthenticated';
    return 'error.generic';
  }
  // fetch() rejects with TypeError when the network/server is unreachable.
  if (error instanceof TypeError) return 'error.network';
  return 'error.generic';
}

export function describeError(t: Translate, error: unknown): string {
  return t(errorMessageKey(error));
}

export function requestIdOf(error: unknown): string | undefined {
  return error instanceof ApiError && error.requestId ? error.requestId : undefined;
}

export function isVersionConflict(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'VERSION_CONFLICT';
}

export function isConflict(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'CONFLICT';
}

/**
 * Field errors from a VALIDATION_FAILED (or field-specific CONFLICT) response:
 * `details.issues[].path` (dotted) and `details.field`. Returns the server message per
 * top-level field; callers may replace it with translated copy.
 */
export function fieldErrorsOf(error: unknown): Record<string, string> {
  if (!(error instanceof ApiError)) return {};
  const out: Record<string, string> = {};
  const issues = Array.isArray(error.details.issues) ? (error.details.issues as unknown[]) : [];
  for (const issue of issues) {
    if (!issue || typeof issue !== 'object') continue;
    const { path, message } = issue as { path?: unknown; message?: unknown };
    const p = Array.isArray(path) ? path.join('.') : typeof path === 'string' ? path : '';
    if (!p) continue;
    out[p] ??= typeof message === 'string' ? message : error.message;
  }
  if (typeof error.details.field === 'string' && !out[error.details.field]) {
    out[error.details.field] = error.message;
  }
  return out;
}
